import React, { useEffect, useMemo, useRef, useState } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Calendar, Users, Layers, FileText, Clock, Building2, LogOut, ChevronDown,
  Menu, X as XIcon, MapPin, CalendarRange, Contact, Receipt, ShieldCheck, Package, History, Bell, UserCircle, TrendingUp, BookOpen,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useBrands } from "../contexts/BrandContext";
import { api } from "../lib/api";
import { getBadgeLocation, NOTIFICATION_BADGE_MAP } from "../lib/menuBadgeMapping";
import NotificationsBell from "./NotificationsBell";
import MenuBadge from "./MenuBadge";
import { useNotificationCounts } from "../contexts/NotificationCountsContext";
import { PLATFORM_TAGLINE } from "../lib/config";
import BrandMark from "./BrandMark";
import AppFooter from "./AppFooter";
import ForcePasswordChangeDialog from "./ForcePasswordChangeDialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "./ui/dropdown-menu";

// Palette colorée mais sobre : chaque entrée de menu reçoit sa propre teinte d'icône
// pour faciliter le repérage visuel sans casser l'identité épurée.
const NAV = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, end: true, testid: "nav-dashboard", roles: ["admin", "manager", "gestionnaire", "collaborateur"], tint: "#0055A4" },
  { to: "/kpi-transactions", label: "KPI & Transactions", icon: TrendingUp, testid: "nav-kpi-transactions", roles: ["admin", "manager", "gestionnaire"], tint: "#F97316" },
  { to: "/planning", label: "Planification collaborateurs", icon: Calendar, testid: "nav-planning", roles: ["admin", "manager"], tint: "#4F46E5" },
  { to: "/agenda-departements", label: "Agenda départements", icon: CalendarRange, testid: "nav-agenda-departements", roles: ["admin", "manager", "gestionnaire"], tint: "#7C3AED" },
  { to: "/historique-vacances", label: "Interventions vacantes", icon: History, testid: "nav-historique-vacances", roles: ["admin", "manager"], tint: "#B91C1C" },
  { to: "/collaborateurs", label: "Collaborateurs", icon: Users, testid: "nav-collaborateurs", roles: ["admin", "manager"], tint: "#DB2777" },
  { to: "/bulletins-remuneration", label: "Bulletins de rémunération", icon: Receipt, testid: "nav-bulletins", roles: ["admin"], tint: "#A855F7" },
  { to: "/contacts", label: "Contacts & bénéficiaires", icon: Contact, testid: "nav-contacts", roles: ["admin", "manager", "gestionnaire"], tint: "#D97706" },
  { to: "/categories", label: "Catégories", icon: Layers, testid: "nav-categories", roles: ["admin", "manager", "gestionnaire"], tint: "#059669" },
  { to: "/sites", label: "Sites", icon: MapPin, testid: "nav-sites", roles: ["admin", "manager", "gestionnaire"], tint: "#0891B2" },
  { to: "/indisponibilites", label: "Indisponibilités", icon: FileText, testid: "nav-indispo", roles: ["admin", "manager", "gestionnaire", "collaborateur"], tint: "#EF4135" },
  { to: "/pointage", label: "Pointage", icon: Clock, testid: "nav-pointage", roles: ["admin", "manager"], tint: "#F59E0B" },
  { to: "/equipements", label: "Matériels & équipements", icon: Package, testid: "nav-equipements", roles: ["admin", "manager", "gestionnaire"], tint: "#0D9488" },
  { to: "/bibliotheque-documents", label: "Bibliothèque de documents", icon: BookOpen, testid: "nav-bibliotheque", roles: ["admin", "manager", "gestionnaire", "collaborateur"], tint: "#9333EA" },
  { to: "/marques", label: "Départements & structures", icon: Building2, testid: "nav-marques", roles: ["admin"], tint: "#52525B" },
  { to: "/audit", label: "Historique de sécurité", icon: ShieldCheck, testid: "nav-audit", roles: ["admin"], tint: "#16A34A" },
  { to: "/notifications", label: "Centre de notifications", icon: Bell, testid: "nav-notifications", roles: ["admin", "manager", "gestionnaire", "collaborateur"], tint: "#0055FF" },
];

// Onglet "Mon profil" rendu séparément en bas du sidebar (juste au-dessus de la zone utilisateur).
const PROFILE_NAV = {
  to: "/mon-dossier", label: "Mon profil", icon: UserCircle, testid: "nav-mon-dossier",
  roles: ["admin", "manager", "gestionnaire", "collaborateur"], tint: "#16A34A",
};

const ROLE_LABELS = {
  admin: "Administrateur",
  manager: "Manager",
  gestionnaire: "Gestionnaire",
  collaborateur: "Collaborateur",
};

export default function Layout() {
  const { user, logout } = useAuth();
  const { brands, selectedBrandId, selectBrand } = useBrands();
  const { byMenu: unreadByMenu } = useNotificationCounts();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const currentBrand = brands.find((b) => b.id === selectedBrandId);
  const items = NAV.filter((n) => !n.roles || n.roles.includes(user?.role));

  // Table de correspondance route → menuTestid (dérivée dynamiquement de NAV + PROFILE_NAV).
  // Utilisée pour marquer automatiquement comme "lues" les notifications correspondant à la
  // page ouverte, faisant disparaître le point rouge de la cloche.
  const routeToMenuTestid = useMemo(() => {
    const map = {};
    for (const n of NAV) map[n.to] = n.testid;
    map[PROFILE_NAV.to] = PROFILE_NAV.testid;
    // Alias route (Indisponibilités est accessible via /conges ET /indisponibilites)
    map["/conges"] = "nav-indispo";
    return map;
  }, []);

  const currentMenuTestid = useMemo(() => {
    const path = location.pathname;
    if (routeToMenuTestid[path]) return routeToMenuTestid[path];
    // Match par préfixe pour les routes imbriquées (ex: /collaborateurs/:id/dossier → nav-collaborateurs)
    let best = null;
    for (const [route, testid] of Object.entries(routeToMenuTestid)) {
      if (route === "/") continue;
      if (path.startsWith(route + "/")) {
        if (!best || route.length > best.route.length) best = { route, testid };
      }
    }
    if (best) return best.testid;
    return path === "/" ? "nav-dashboard" : null;
  }, [location.pathname, routeToMenuTestid]);

  // Auto-marquage comme "lu" des notifications de la rubrique ouverte : dès que
  // l'utilisateur navigue sur une page, toutes les notifications non-lues de
  // types rattachés à cette rubrique (selon menuBadgeMapping) sont marquées
  // comme lues côté serveur. Le point rouge de la cloche disparaît sans action
  // supplémentaire du collaborateur. Une référence en mémoire évite d'appeler
  // l'endpoint plusieurs fois pour le même menu au sein d'une session.
  const alreadyMarkedRef = useRef(new Set());
  useEffect(() => {
    if (!currentMenuTestid || !user) return;
    if (alreadyMarkedRef.current.has(currentMenuTestid)) return;
    const role = user?.role;
    const types = Object.entries(NOTIFICATION_BADGE_MAP)
      .filter(([type]) => getBadgeLocation(type, role)?.menuTestid === currentMenuTestid)
      .map(([type]) => type);
    if (types.length === 0) return;
    alreadyMarkedRef.current.add(currentMenuTestid);
    api
      .put("/notifications/mark-read-by-types", { types })
      .then((res) => {
        const modified = res?.data?.modified || 0;
        if (modified > 0) {
          try {
            window.dispatchEvent(new CustomEvent("plania:notifications-changed"));
          } catch {
            /* noop */
          }
        }
      })
      .catch(() => {
        // Autoriser une nouvelle tentative si l'appel a échoué (réseau, session expirée…)
        alreadyMarkedRef.current.delete(currentMenuTestid);
      });
  }, [currentMenuTestid, user]);

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  const closeMobile = () => setMobileOpen(false);

  const SidebarContent = ({ isMobile = false }) => (
    <>
      <div className="h-16 flex items-center justify-between px-5 border-b border-zinc-200 relative">
        <BrandMark size="sm" showText={true} monochrome uppercase />
        {isMobile && (
          <button onClick={closeMobile} className="text-zinc-500 hover:text-zinc-900" data-testid="close-mobile-sidebar">
            <XIcon size={18} />
          </button>
        )}
        {/* Accent tricolore subtil sous la barre du logo */}
        <span className="absolute left-0 right-0 bottom-0 h-px flex" aria-hidden="true">
          <span className="flex-1 bg-[#0055A4]" />
          <span className="flex-1 bg-zinc-200" />
          <span className="flex-1 bg-[#EF4135]" />
        </span>
      </div>

      <nav className="flex-1 py-5 px-3 space-y-0.5 overflow-y-auto">
        <p className="label-uppercase px-3 mb-2">Navigation</p>
        {items.map((item) => {
          const Icon = item.icon;
          const badgeCount = unreadByMenu[item.testid] || 0;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              onClick={closeMobile}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 text-sm rounded-sm transition-colors ${
                  isActive
                    ? "bg-[#EFF6FF] text-[#0044CC] font-medium"
                    : "text-zinc-700 hover:bg-zinc-50"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon
                    size={16}
                    strokeWidth={1.75}
                    style={isActive ? undefined : { color: item.tint }}
                  />
                  <span className="flex-1 truncate">{item.label}</span>
                  <MenuBadge
                    count={badgeCount}
                    testid={`menu-badge-${item.testid}`}
                  />
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="p-3 border-t border-zinc-200 space-y-1">
        {PROFILE_NAV.roles.includes(user?.role) && (
          <NavLink
            to={PROFILE_NAV.to}
            data-testid={PROFILE_NAV.testid}
            onClick={closeMobile}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2 text-sm rounded-sm transition-colors ${
                isActive
                  ? "bg-[#EFF6FF] text-[#0044CC] font-medium"
                  : "text-zinc-700 hover:bg-zinc-50"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <PROFILE_NAV.icon
                  size={16}
                  strokeWidth={1.75}
                  style={isActive ? undefined : { color: PROFILE_NAV.tint }}
                />
                <span className="flex-1 truncate">{PROFILE_NAV.label}</span>
                <MenuBadge
                  count={unreadByMenu[PROFILE_NAV.testid] || 0}
                  testid={`menu-badge-${PROFILE_NAV.testid}`}
                />
              </>
            )}
          </NavLink>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="w-full flex items-center gap-2.5 p-2 hover:bg-zinc-50 rounded-sm text-left"
              data-testid="user-menu-trigger"
            >
              <div className="h-8 w-8 bg-zinc-900 text-white flex items-center justify-center text-xs font-medium">
                {(user?.name || "U").slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-zinc-900 truncate">{user?.name}</p>
                <p className="text-[11px] text-zinc-500 truncate font-mono">{ROLE_LABELS[user?.role] || user?.role}</p>
              </div>
              <ChevronDown size={14} className="text-zinc-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="font-mono text-xs">{user?.email}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onLogout} data-testid="logout-btn" className="text-red-600">
              <LogOut size={14} className="mr-2" />
              Se déconnecter
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );

  return (
    <div className="h-screen flex bg-[#F4F4F5] overflow-hidden">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 bg-white border-r border-zinc-200 flex-col h-screen sticky top-0" data-testid="sidebar">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={closeMobile} />
          <aside className="absolute left-0 top-0 h-full w-72 bg-white border-r border-zinc-200 flex flex-col shadow-xl">
            <SidebarContent isMobile />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Brand banner (2 bandes, visible sur toutes les pages) */}
        <div className="bg-white border-b border-zinc-200 sticky top-0 z-30" data-testid="brand-banner">
          <div className="px-4 sm:px-6 py-2.5 grid grid-cols-3 items-center border-b border-zinc-100">
            <div className="justify-self-start">
              <button
                onClick={() => setMobileOpen(true)}
                className="lg:hidden h-9 w-9 flex items-center justify-center border border-zinc-200 rounded-sm hover:bg-zinc-50"
                data-testid="open-mobile-sidebar-top"
                aria-label="Ouvrir le menu"
              >
                <Menu size={16} />
              </button>
            </div>
            <div className="justify-self-center">
              <BrandMark size="xl" monochrome uppercase />
            </div>
            <div />
          </div>
          {/* Accent tricolore (Bleu — Blanc — Rouge) — même style que la page de connexion */}
          <div className="h-1 flex" aria-hidden="true" data-testid="brand-tricolor">
            <span className="flex-1 bg-[#0055A4]" />
            <span className="flex-1 bg-white" />
            <span className="flex-1 bg-[#EF4135]" />
          </div>
          <div className="px-4 sm:px-6 py-1.5 bg-zinc-50/60 text-center">
            <span
              className="text-[11px] uppercase tracking-[0.18em] text-zinc-500"
              data-testid="platform-tagline-banner"
            >
              {PLATFORM_TAGLINE}
            </span>
          </div>
        </div>

        {/* Topbar (sélecteur département + actions) */}
        <header className="h-14 bg-white border-b border-zinc-200 flex items-center justify-between px-4 sm:px-6 sticky top-[112px] z-20">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            {user?.role !== "collaborateur" && (
            <>
            <p className="label-uppercase hidden sm:block">Section</p>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 px-2 sm:px-3 py-1.5 border border-zinc-200 rounded-sm hover:bg-zinc-50 max-w-[200px] sm:max-w-none"
                  data-testid="brand-selector"
                >
                  {currentBrand?.logo_url ? (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${currentBrand.logo_url}`}
                      alt=""
                      className="h-5 w-5 object-cover rounded-sm border border-zinc-200 flex-shrink-0"
                    />
                  ) : currentBrand ? (
                    <span
                      className="h-2 w-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: currentBrand.color || "#0055FF" }}
                    />
                  ) : null}
                  <span className="text-xs sm:text-sm font-medium text-zinc-900 truncate">
                    {currentBrand?.name || "Tous les départements"}
                  </span>
                  <ChevronDown size={14} className="text-zinc-400 flex-shrink-0" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-72">
                <DropdownMenuLabel className="label-uppercase">Départements / Structures</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => selectBrand("")} data-testid="brand-option-all">
                  <span className="h-2 w-2 rounded-full bg-zinc-400 mr-2" />
                  Tous les départements
                </DropdownMenuItem>
                {brands.map((b) => (
                  <DropdownMenuItem
                    key={b.id}
                    onClick={() => selectBrand(b.id)}
                    data-testid={`brand-option-${b.id}`}
                  >
                    {b.logo_url ? (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${b.logo_url}`}
                        alt=""
                        className="h-4 w-4 object-cover rounded-sm border border-zinc-200 mr-2"
                      />
                    ) : (
                      <span
                        className="h-2 w-2 rounded-full mr-2"
                        style={{ backgroundColor: b.color || "#0055FF" }}
                      />
                    )}
                    <span className="flex-1">{b.name}</span>
                    {b.timezone && (
                      <span className="font-mono text-[10px] text-zinc-400 ml-2 hidden sm:inline">{b.timezone}</span>
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            </>
            )}
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <p className="font-mono text-xs text-zinc-500 hidden md:block">
              {(() => {
                const d = new Date();
                const wd = d.toLocaleDateString("fr-FR", { weekday: "long" });
                const dd = String(d.getDate()).padStart(2, "0");
                const mm = String(d.getMonth() + 1).padStart(2, "0");
                return `${wd} ${dd}/${mm}/${d.getFullYear()}`;
              })()}
            </p>
            {currentBrand?.timezone && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-zinc-50 border border-zinc-200 rounded-sm font-mono text-[10px] text-zinc-600 tracking-wider uppercase hidden sm:inline-flex" data-testid="brand-timezone">
                {currentBrand.timezone}
              </span>
            )}
            <NotificationsBell />
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 animate-fade-in-up" data-testid="main-content">
          <Outlet />
        </main>
        <AppFooter />
      </div>
      <ForcePasswordChangeDialog />
    </div>
  );
}
